import { type CommonConvoAIInput, streamConvoAI } from '../provider/prompt-requests/convo-ai';
import type { IntentSchemaId } from '../provider/prompt-requests/types';

import type { EditorPluginAIConfigItemMarkdown } from './config-items';
import { getSlice } from './get-slice';
import { sliceOrNodeToMarkdown } from './slice-or-node-to-markdown';

export function makeEmptySelectionGetInitialContext({
	intentSchemaId,
}: {
	intentSchemaId: IntentSchemaId;
}): EditorPluginAIConfigItemMarkdown<'empty'>['getInitialContext'] {
	return ({ editorView, intl, updateIdMap, mentionMap }) => {
		const document = sliceOrNodeToMarkdown({
			node: editorView.state.doc,
			editorView,
			convertTo: 'markdown-plus',
			updateIdMap,
			selectionType: 'empty',
			mentionMap,
		});

		return {
			document: document.markdown,
			userLocale: intl.locale,
			intentSchemaId,
			contentStatistics: document.contentStatistics,
		};
	};
}

export function makeRangeSelectionGetInitialContext({
	intentSchemaId,
	sendFullDocumentForRangeSelection = true,
}: {
	intentSchemaId: IntentSchemaId;
	sendFullDocumentForRangeSelection?: boolean;
}): EditorPluginAIConfigItemMarkdown<'range'>['getInitialContext'] {
	return ({ editorView, positions, intl, updateIdMap, mentionMap }) => {
		const selection = sliceOrNodeToMarkdown({
			slice: getSlice(editorView, positions),
			editorView,
			convertTo: 'markdown-plus',
			updateIdMap,
			selectionType: 'range',
			mentionMap,
		});

		const document = sliceOrNodeToMarkdown({
			node: editorView.state.doc,
			editorView,
			convertTo: 'markdown-plus',
			updateIdMap,
			selectionType: 'range',
			mentionMap,
		});

		return {
			selection: selection.markdown,
			document: sendFullDocumentForRangeSelection ? document.markdown : undefined,
			userLocale: intl.locale,
			intentSchemaId,
			contentStatistics: selection.contentStatistics,
		};
	};
}

export function makeEmptySelectionTriggerPromptRequest({
	intentSchemaId,
}: {
	intentSchemaId: CommonConvoAIInput['intentSchemaId'];
}): EditorPluginAIConfigItemMarkdown<'empty'>['triggerPromptRequest'] {
	return ({ initialContext, editorSchema, analyticsContext }) => {
		return streamConvoAI({
			aiSessionId: analyticsContext?.aiSessionId,
			userLocale: initialContext.userLocale,
			intentSchemaId,
			editorSchema,
			fullDocument: initialContext.document,
		});
	};
}

export function makeRangeSelectionTriggerPromptRequest({
	intentSchemaId,
	sendFullDocumentForRangeSelection = true,
}: {
	intentSchemaId: CommonConvoAIInput['intentSchemaId'];
	sendFullDocumentForRangeSelection?: boolean;
}): EditorPluginAIConfigItemMarkdown<'range'>['triggerPromptRequest'] {
	return ({ initialContext, editorSchema, analyticsContext }) => {
		return streamConvoAI({
			aiSessionId: analyticsContext?.aiSessionId,
			userLocale: initialContext.userLocale,
			intentSchemaId,
			editorSchema,
			currentSelection: initialContext.selection,
			fullDocument: sendFullDocumentForRangeSelection ? initialContext.document : undefined,
		});
	};
}
